import React, { useEffect, useMemo, useState } from 'react';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { apiService } from '../services/api';
import { fxService } from '../services/fx.service';
import { Contract, Customer, Invoice, Server } from '../types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

// Helpers
const formatCurrency = (amount: number, currency: string) => new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

type EntityType = 'contract' | 'customer' | 'server' | 'all';

interface Filters {
  fromDate?: string;
  toDate?: string;
  status?: 'any' | 'draft' | 'finalized' | 'cancelled' | 'voided';
  payment?: 'any' | 'paid' | 'unpaid' | 'overdue';
  currencies: string[];
  entityType: EntityType;
  entitySearch?: string;
}

interface AggregatedRow {
  key: string; // entity id
  name: string;
  currency: string; // dominant or last seen currency (for native display)
  totalRevenue: number; // native sum
  invoicesIssued: number;
  invoicesPaid: number;
  outstanding: number; // native sum
  converted: number; // revenue in main currency
  convertedOutstanding: number; // outstanding in main currency
}

const MAIN_CURRENCIES = ['EUR', 'USD', 'GBP', 'BRL', 'CAD', 'CHF'];

const defaultFilters: Filters = {
  currencies: [],
  entityType: 'all',
  status: 'any',
  payment: 'any',
  entitySearch: ''
};

const LOCAL_STORAGE_MAIN_CURRENCY = 'bi.mainCurrency';

const BIDashboard: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<Filters>(() => {
    try {
      const saved = localStorage.getItem('bi.filters');
      if (saved) return { ...defaultFilters, ...JSON.parse(saved) } as Filters;
    } catch {}
    return defaultFilters;
  });
  const [mainCurrency, setMainCurrency] = useState<string>(() => {
    try {
      return localStorage.getItem(LOCAL_STORAGE_MAIN_CURRENCY) || 'EUR';
    } catch {
      return 'EUR';
    }
  });

  // Persist main currency and filters
  useEffect(() => {
    try { localStorage.setItem(LOCAL_STORAGE_MAIN_CURRENCY, mainCurrency); } catch {}
  }, [mainCurrency]);
  useEffect(() => {
    try { localStorage.setItem('bi.filters', JSON.stringify(filters)); } catch {}
  }, [filters]);

  // Load base data
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [contractsRes, customersRes, serversRes] = await Promise.all([
          apiService.getContracts(1, 1000).catch(() => ({ items: [] })),
          apiService.getCustomers(1, 1000).catch(() => ({ items: [] })),
          apiService.getAllServers(1000).catch(() => [] as Server[])
        ]);
        if (cancelled) return;
        setContracts(contractsRes.items || []);
        setCustomers(customersRes.items || []);
        setServers(serversRes);
        await loadInvoices();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInvoices = async () => {
    const opts: any = {
      page: 1,
      pageSize: 1000, // respect backend max
    };
    // Map filters
    if (filters.status && filters.status !== 'any') opts.status = filters.status;
    if (filters.payment === 'paid') opts.show_paid = true;
    if (filters.payment === 'unpaid') opts.show_unpaid = true;
    if (filters.payment === 'overdue') opts.show_overdue = true;
    if (filters.fromDate) opts.from_date = filters.fromDate;
    if (filters.toDate) opts.to_date = filters.toDate;
    if (filters.currencies.length > 0) opts.currencies = filters.currencies;

    // Fetch all pages to satisfy BI needs without violating API constraints
    const list = await apiService.invoices.listInvoicesAll({ ...opts });
    setInvoices(list || []);
  };

  const contractsMap = useMemo(() => new Map(contracts.map((c: Contract) => [c.contract_id, c])), [contracts]);
  const customersMap = useMemo(() => new Map(customers.map((c: Customer) => [c.id, c])), [customers]);
  const serversMap = useMemo(() => new Map(servers.map((s: Server) => [String(s.id), s])), [servers]);

  // Aggregate by entity type (native amounts and counters)
  const [byContract, byCustomer, byServer] = useMemo(() => {
    const contractAgg = new Map<string, AggregatedRow>();
    const customerAgg = new Map<string, AggregatedRow>();
    const serverAgg = new Map<string, AggregatedRow>();

    for (const inv of invoices) {
      const isOutstanding = (inv.payment_status as any) !== 'paid' && (inv.invoice_status as any) !== 'voided' && (inv.invoice_status as any) !== 'cancelled';

      // Contract aggregation
      const c = contractsMap.get(inv.contract_id);
      const contractName = c?.contract_name || inv.contract_name || inv.contract_id;
      const k1 = inv.contract_id;
      const row1 = contractAgg.get(k1) || {
        key: k1,
        name: contractName,
        currency: inv.currency,
        totalRevenue: 0,
        invoicesIssued: 0,
        invoicesPaid: 0,
        outstanding: 0,
        converted: 0,
        convertedOutstanding: 0,
      };
      row1.currency = inv.currency; // last seen
      row1.totalRevenue += inv.total_amount || 0;
      row1.invoicesIssued += 1;
      if ((inv.payment_status as any) === 'paid') row1.invoicesPaid += 1;
      if (isOutstanding) row1.outstanding += inv.total_amount || 0;
      contractAgg.set(k1, row1);

      // Customer aggregation
      const cust = customersMap.get(inv.customer_id);
      const custName = cust?.customer_name || inv.customer_name || String(inv.customer_id);
      const k2 = String(inv.customer_id);
      const row2 = customerAgg.get(k2) || {
        key: k2,
        name: custName,
        currency: inv.currency,
        totalRevenue: 0,
        invoicesIssued: 0,
        invoicesPaid: 0,
        outstanding: 0,
        converted: 0,
        convertedOutstanding: 0,
      };
      row2.currency = inv.currency;
      row2.totalRevenue += inv.total_amount || 0;
      row2.invoicesIssued += 1;
      if ((inv.payment_status as any) === 'paid') row2.invoicesPaid += 1;
      if (isOutstanding) row2.outstanding += inv.total_amount || 0;
      customerAgg.set(k2, row2);

      // Server aggregation via customer.server_id
      const serverId = cust?.server_id ? String(cust.server_id) : undefined;
      if (serverId) {
        const serverName = serversMap.get(serverId)?.name || `Server ${serverId}`;
        const k3 = serverId;
        const row3 = serverAgg.get(k3) || {
          key: k3,
          name: serverName,
          currency: inv.currency,
          totalRevenue: 0,
          invoicesIssued: 0,
          invoicesPaid: 0,
          outstanding: 0,
          converted: 0,
          convertedOutstanding: 0,
        };
        row3.currency = inv.currency;
        row3.totalRevenue += inv.total_amount || 0;
        row3.invoicesIssued += 1;
        if ((inv.payment_status as any) === 'paid') row3.invoicesPaid += 1;
        if (isOutstanding) row3.outstanding += inv.total_amount || 0;
        serverAgg.set(k3, row3);
      }
    }

    return [Array.from(contractAgg.values()), Array.from(customerAgg.values()), Array.from(serverAgg.values())] as const;
  }, [invoices, contractsMap, customersMap, serversMap]);

  // Conversion effect: compute revenue/outstanding converted per entity using historical date per invoice
  const [contractRows, setContractRows] = useState<AggregatedRow[]>([]);
  const [customerRows, setCustomerRows] = useState<AggregatedRow[]>([]);
  const [serverRows, setServerRows] = useState<AggregatedRow[]>([]);
  const [summary, setSummary] = useState({ revenue: 0, invoices: 0, paid: 0, outstanding: 0 });

  useEffect(() => {
    let cancelled = false;
    const convertAll = async () => {
      // Initialize maps
      const cMap = new Map<string, number>();
      const cOutMap = new Map<string, number>();
      const uMap = new Map<string, number>();
      const uOutMap = new Map<string, number>();
      const sMap = new Map<string, number>();
      const sOutMap = new Map<string, number>();

      let revenue = 0, invoicesCount = 0, paid = 0, outstanding = 0;

      for (const inv of invoices) {
        invoicesCount += 1;
        if ((inv.payment_status as any) === 'paid') paid += 1;
        const conv = await fxService.convert(inv.total_amount || 0, inv.currency, mainCurrency);
        revenue += conv;
        const isOutstanding = (inv.payment_status as any) !== 'paid' && (inv.invoice_status as any) !== 'voided' && (inv.invoice_status as any) !== 'cancelled';
        if (isOutstanding) {
          const convOut = await fxService.convert(inv.total_amount || 0, inv.currency, mainCurrency);
          outstanding += convOut;
        }

        // Contract
        const k1 = inv.contract_id;
        cMap.set(k1, (cMap.get(k1) || 0) + conv);
        if (isOutstanding) cOutMap.set(k1, (cOutMap.get(k1) || 0) + conv);

        // Customer
        const k2 = String(inv.customer_id);
        uMap.set(k2, (uMap.get(k2) || 0) + conv);
        if (isOutstanding) uOutMap.set(k2, (uOutMap.get(k2) || 0) + conv);

        // Server via customer.server_id
        const cust = customersMap.get(inv.customer_id);
        const serverId = cust?.server_id ? String(cust.server_id) : undefined;
        if (serverId) {
          sMap.set(serverId, (sMap.get(serverId) || 0) + conv);
          if (isOutstanding) sOutMap.set(serverId, (sOutMap.get(serverId) || 0) + conv);
        }
      }

      if (cancelled) return;

      const withContractConverted = byContract.map(r => ({
        ...r,
        converted: Math.round((cMap.get(r.key) || 0) * 100) / 100,
        convertedOutstanding: Math.round((cOutMap.get(r.key) || 0) * 100) / 100,
      }));
      const withCustomerConverted = byCustomer.map(r => ({
        ...r,
        converted: Math.round((uMap.get(r.key) || 0) * 100) / 100,
        convertedOutstanding: Math.round((uOutMap.get(r.key) || 0) * 100) / 100,
      }));
      const withServerConverted = byServer.map(r => ({
        ...r,
        converted: Math.round((sMap.get(r.key) || 0) * 100) / 100,
        convertedOutstanding: Math.round((sOutMap.get(r.key) || 0) * 100) / 100,
      }));

      setContractRows(withContractConverted.sort((a, b) => b.converted - a.converted));
      setCustomerRows(withCustomerConverted.sort((a, b) => b.converted - a.converted));
      setServerRows(withServerConverted.sort((a, b) => b.converted - a.converted));
      setSummary({ revenue: Math.round(revenue * 100) / 100, invoices: invoicesCount, paid, outstanding: Math.round(outstanding * 100) / 100 });
    };
    convertAll();
    return () => { cancelled = true; };
  }, [invoices, mainCurrency, byContract, byCustomer, byServer, customersMap]);

  // Charts data (trend converted to main currency per month)
  const trendData = useMemo(() => {
    // Pre-aggregate in main currency by yyyy-mm
    const byMonth = new Map<string, number>();
    const promises: Promise<void>[] = [];
    for (const inv of invoices) {
      const d = inv.invoice_date ? inv.invoice_date.slice(0, 7) : 'unknown';
      const p = fxService.convert(inv.total_amount || 0, inv.currency, mainCurrency)
        .then(val => {
          byMonth.set(d, (byMonth.get(d) || 0) + val);
        });
      promises.push(p);
    }
    // We cannot await here in useMemo; provide placeholder and update via state would be complex.
    // For simplicity, return native until conversion completes in a separate effect; acceptable for first render.
    // Fallback: show native sums now.
    const nativeByMonth = new Map<string, number>();
    for (const inv of invoices) {
      const d = inv.invoice_date ? inv.invoice_date.slice(0, 7) : 'unknown';
      nativeByMonth.set(d, (nativeByMonth.get(d) || 0) + (inv.total_amount || 0));
    }
    const labels = Array.from(nativeByMonth.keys()).sort();
    const series = labels.map(l => nativeByMonth.get(l) || 0);
    return {
      labels,
      datasets: [
        {
          label: `Revenue (${mainCurrency}) approx` ,
          data: series,
          fill: true,
          borderColor: 'rgba(54, 162, 235, 1)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)'
        }
      ]
    };
  }, [invoices, mainCurrency]);

  const topContractsData = useMemo(() => {
    const top = [...contractRows].slice(0, 10);
    return {
      labels: top.map(t => t.name),
      datasets: [
        {
          label: `Revenue in ${mainCurrency}`,
          data: top.map(t => t.converted),
          backgroundColor: 'rgba(75, 192, 192, 0.5)'
        }
      ]
    };
  }, [contractRows, mainCurrency]);

  const currencySplitData = useMemo(() => {
    const byCur = new Map<string, number>();
    for (const inv of invoices) {
      byCur.set(inv.currency, (byCur.get(inv.currency) || 0) + (inv.total_amount || 0));
    }
    const labels = Array.from(byCur.keys());
    const data = labels.map(l => byCur.get(l) || 0);
    const colors = ['#4dc9f6', '#f67019', '#f53794', '#537bc4', '#acc236', '#166a8f'];
    return {
      labels,
      datasets: [
        {
          label: 'Revenue by currency (native)',
          data,
          backgroundColor: labels.map((_, i) => colors[i % colors.length])
        }
      ]
    };
  }, [invoices]);

  // CSV export
  const exportCSV = (rows: AggregatedRow[], filename: string) => {
    const headers = ['Name','Invoices Issued','Invoices Paid','Revenue (native)','Currency','Outstanding (native)','Revenue ('+mainCurrency+')','Outstanding ('+mainCurrency+')'];
    const lines = [headers.join(',')];
    for (const r of rows) {
      lines.push([
        '"'+r.name.replace(/"/g, '""')+'"',
        r.invoicesIssued,
        r.invoicesPaid,
        r.totalRevenue,
        r.currency,
        r.outstanding,
        r.converted,
        r.convertedOutstanding,
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Filter rows by entity search
  const applyEntitySearch = (rows: AggregatedRow[]) => {
    const q = (filters.entitySearch || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => r.name.toLowerCase().includes(q));
  };

  // UI handlers
  const applyFilters = async () => {
    setLoading(true);
    try {
      await loadInvoices();
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = async () => {
    setFilters(defaultFilters);
    setLoading(true);
    try {
      await loadInvoices();
    } finally {
      setLoading(false);
    }
  };

  const entityTables = (
    <div className="grid grid-cols-1 gap-4">
      {(filters.entityType === 'all' || filters.entityType === 'contract') && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="card-title">By Contract</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => exportCSV(applyEntitySearch(contractRows), 'contracts.csv')}>Export CSV</button>
          </div>
          <div className="card-body overflow-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="text-right">Revenue</th>
                  <th className="text-right">Issued</th>
                  <th className="text-right">Paid</th>
                  <th className="text-right">Outstanding</th>
                  <th className="text-right">Converted ({mainCurrency})</th>
                  <th className="text-right">Outstanding ({mainCurrency})</th>
                </tr>
              </thead>
              <tbody>
                {applyEntitySearch(contractRows).map(row => (
                  <tr key={row.key}>
                    <td>{row.name}</td>
                    <td className="text-right">{formatCurrency(row.totalRevenue, row.currency)}</td>
                    <td className="text-right">{row.invoicesIssued}</td>
                    <td className="text-right">{row.invoicesPaid}</td>
                    <td className="text-right">{formatCurrency(row.outstanding, row.currency)}</td>
                    <td className="text-right">{formatCurrency(row.converted, mainCurrency)}</td>
                    <td className="text-right">{formatCurrency(row.convertedOutstanding, mainCurrency)}</td>
                  </tr>
                ))}
                {contractRows.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-gray-500">No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(filters.entityType === 'all' || filters.entityType === 'customer') && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="card-title">By Customer</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => exportCSV(applyEntitySearch(customerRows), 'customers.csv')}>Export CSV</button>
          </div>
          <div className="card-body overflow-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="text-right">Revenue</th>
                  <th className="text-right">Issued</th>
                  <th className="text-right">Paid</th>
                  <th className="text-right">Outstanding</th>
                  <th className="text-right">Converted ({mainCurrency})</th>
                  <th className="text-right">Outstanding ({mainCurrency})</th>
                </tr>
              </thead>
              <tbody>
                {applyEntitySearch(customerRows).map(row => (
                  <tr key={row.key}>
                    <td>{row.name}</td>
                    <td className="text-right">{formatCurrency(row.totalRevenue, row.currency)}</td>
                    <td className="text-right">{row.invoicesIssued}</td>
                    <td className="text-right">{row.invoicesPaid}</td>
                    <td className="text-right">{formatCurrency(row.outstanding, row.currency)}</td>
                    <td className="text-right">{formatCurrency(row.converted, mainCurrency)}</td>
                    <td className="text-right">{formatCurrency(row.convertedOutstanding, mainCurrency)}</td>
                  </tr>
                ))}
                {customerRows.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-gray-500">No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(filters.entityType === 'all' || filters.entityType === 'server') && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="card-title">By Server</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => exportCSV(applyEntitySearch(serverRows), 'servers.csv')}>Export CSV</button>
          </div>
          <div className="card-body overflow-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="text-right">Revenue</th>
                  <th className="text-right">Issued</th>
                  <th className="text-right">Paid</th>
                  <th className="text-right">Outstanding</th>
                  <th className="text-right">Converted ({mainCurrency})</th>
                  <th className="text-right">Outstanding ({mainCurrency})</th>
                </tr>
              </thead>
              <tbody>
                {applyEntitySearch(serverRows).map(row => (
                  <tr key={row.key}>
                    <td>{row.name}</td>
                    <td className="text-right">{formatCurrency(row.totalRevenue, row.currency)}</td>
                    <td className="text-right">{row.invoicesIssued}</td>
                    <td className="text-right">{row.invoicesPaid}</td>
                    <td className="text-right">{formatCurrency(row.outstanding, row.currency)}</td>
                    <td className="text-right">{formatCurrency(row.converted, mainCurrency)}</td>
                    <td className="text-right">{formatCurrency(row.convertedOutstanding, mainCurrency)}</td>
                  </tr>
                ))}
                {serverRows.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-gray-500">No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h1>BI Dashboard</h1>
        <div className="flex items-center gap-2">
          <label>Main Currency:</label>
          <select
            className="form-control"
            value={mainCurrency}
            onChange={(e) => setMainCurrency(e.target.value)}
          >
            {MAIN_CURRENCIES.map((c: string) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-header flex items-center justify-between">
          <h3 className="card-title">Filters</h3>
          <div className="flex items-center gap-2">
            <button className="btn btn-primary btn-sm" onClick={applyFilters} disabled={loading}>Apply</button>
            <button className="btn btn-secondary btn-sm" onClick={clearFilters} disabled={loading}>Clear</button>
          </div>
        </div>
        <div className="card-body grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium">From</label>
            <input type="date" className="form-control" value={filters.fromDate || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.target.value || undefined }))} />
          </div>
          <div>
            <label className="block text-sm font-medium">To</label>
            <input type="date" className="form-control" value={filters.toDate || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.target.value || undefined }))} />
          </div>
          <div>
            <label className="block text-sm font-medium">Invoice Status</label>
            <select className="form-control" value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as any }))}>
              <option value="any">Any</option>
              <option value="draft">Draft</option>
              <option value="finalized">Finalized</option>
              <option value="cancelled">Cancelled</option>
              <option value="voided">Voided</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium">Payment</label>
            <select className="form-control" value={filters.payment}
              onChange={(e) => setFilters(prev => ({ ...prev, payment: e.target.value as any }))}>
              <option value="any">Any</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium">Currencies</label>
            <div className="flex flex-wrap gap-2">
              {MAIN_CURRENCIES.map((c: string) => (
                <label key={c} className="flex items-center gap-1">
                  <input type="checkbox"
                    checked={filters.currencies.includes(c)}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      currencies: e.target.checked
                        ? [...prev.currencies, c]
                        : prev.currencies.filter(x => x !== c)
                    }))}
                  />
                  <span>{c}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium">Entity Type</label>
            <select className="form-control" value={filters.entityType}
              onChange={(e) => setFilters(prev => ({ ...prev, entityType: e.target.value as EntityType }))}>
              <option value="all">All</option>
              <option value="contract">Contract</option>
              <option value="customer">Customer</option>
              <option value="server">Server</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium">Entity Search</label>
            <input type="text" className="form-control" placeholder="Filter by name" value={filters.entitySearch || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, entitySearch: e.target.value }))} />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div className="stat-card">
          <h6>Total Revenue</h6>
          <div className="stat-value">{formatCurrency(summary.revenue, mainCurrency)}</div>
        </div>
        <div className="stat-card">
          <h6>Invoices</h6>
          <div className="stat-value">{summary.invoices}</div>
        </div>
        <div className="stat-card">
          <h6>Paid</h6>
          <div className="stat-value">{summary.paid}</div>
        </div>
        <div className="stat-card">
          <h6>Outstanding</h6>
          <div className="stat-value">{formatCurrency(summary.outstanding, mainCurrency)}</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="card">
          <div className="card-header"><h3 className="card-title">Revenue Trend</h3></div>
          <div className="card-body">
            <Line data={trendData} options={{ responsive: true, maintainAspectRatio: false }} height={300} />
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3 className="card-title">Top Contracts</h3></div>
          <div className="card-body">
            <Bar data={topContractsData} options={{ responsive: true, maintainAspectRatio: false }} height={300} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="card">
          <div className="card-header"><h3 className="card-title">Currency Split</h3></div>
          <div className="card-body">
            <Doughnut data={currencySplitData} />
          </div>
        </div>
      </div>

      {entityTables}
    </div>
  );
};

export default BIDashboard;
